import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import './PaymentCollection.css';

const PaymentCollectionDashboard = () => {
  const [activeTab, setActiveTab] = useState('create-link');
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [receivedPayments, setReceivedPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [linkData, setLinkData] = useState({
    amount: '',
    description: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    callbackUrl: ''
  });

  useEffect(() => {
    if (activeTab === 'payment-links') {
      loadPaymentLinks();
    } else if (activeTab === 'received-payments') {
      loadReceivedPayments();
    }
  }, [activeTab]);

  const loadPaymentLinks = async () => {
    try {
      setLoading(true);
      const links = await apiService.getPaymentLinks();
      setPaymentLinks(links);
    } catch (error) {
      console.error('Error loading payment links:', error);
      alert('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  const loadReceivedPayments = async () => {
    try {
      setLoading(true);
      const payments = await apiService.getReceivedPayments();
      setReceivedPayments(payments.content || []);
    } catch (error) {
      console.error('Error loading received payments:', error);
      alert('Failed to load received payments');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLinkData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiService.createPaymentLink(linkData);
      alert(`Payment link created successfully!\nURL: ${response.shortUrl}`);
      setLinkData({
        amount: '',
        description: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        callbackUrl: ''
      });
      if (activeTab === 'payment-links') {
        loadPaymentLinks();
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      alert('Failed to create payment link: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      alert('Payment link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Payment link copied to clipboard!');
    });
  };

  const handleCancelLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to cancel this payment link?')) {
      return;
    }

    try {
      await apiService.cancelPaymentLink(linkId);
      alert('Payment link cancelled successfully');
      loadPaymentLinks();
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      alert('Failed to cancel payment link');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div className="payment-collection-container">
      <h2>Payment Collection Dashboard</h2>
      
      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'create-link' ? 'active' : ''}`}
          onClick={() => setActiveTab('create-link')}
        >
          Create Payment Link
        </button>
        <button 
          className={`tab-button ${activeTab === 'payment-links' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment-links')}
        >
          Payment Links ({paymentLinks.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'received-payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('received-payments')}
        >
          Received Payments ({receivedPayments.length})
        </button>
      </div>

      {activeTab === 'create-link' && (
        <div className="tab-content">
          <div className="create-link-section">
            <h3>Generate Payment Link for Customer</h3>
            <p>Create a secure payment link to collect money from customers. They'll receive the link via email and SMS.</p>
            
            <form onSubmit={handleCreateLink} className="link-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Amount (INR):</label>
                  <input
                    type="number"
                    name="amount"
                    value={linkData.amount}
                    onChange={handleInputChange}
                    min="1"
                    step="0.01"
                    required
                    disabled={loading}
                    placeholder="e.g., 1000.00"
                  />
                </div>
                
                <div className="form-group">
                  <label>Customer Name:</label>
                  <input
                    type="text"
                    name="customerName"
                    value={linkData.customerName}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Customer's full name"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Customer Email:</label>
                  <input
                    type="email"
                    name="customerEmail"
                    value={linkData.customerEmail}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="customer@example.com"
                  />
                </div>
                
                <div className="form-group">
                  <label>Customer Phone:</label>
                  <input
                    type="tel"
                    name="customerPhone"
                    value={linkData.customerPhone}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="+91xxxxxxxxxx"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description"
                  value={linkData.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                  disabled={loading}
                  placeholder="What is this payment for?"
                />
              </div>
              
              <div className="form-group">
                <label>Callback URL (Optional):</label>
                <input
                  type="url"
                  name="callbackUrl"
                  value={linkData.callbackUrl}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="https://yoursite.com/payment-success"
                />
                <small className="help-text">
                  URL where customer will be redirected after successful payment
                </small>
              </div>
              
              <button type="submit" disabled={loading} className="create-link-button">
                {loading ? 'Creating Link...' : `Create Payment Link for ${formatCurrency(linkData.amount || 0)}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'payment-links' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>Your Payment Links</h3>
            <button 
              onClick={loadPaymentLinks}
              disabled={loading}
              className="refresh-button"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          
          {loading ? (
            <div className="loading">Loading payment links...</div>
          ) : (
            <div className="links-list">
              {paymentLinks.length === 0 ? (
                <div className="no-data">
                  <h4>No payment links created yet</h4>
                  <p>Create your first payment link to start collecting payments from customers.</p>
                  <button 
                    onClick={() => setActiveTab('create-link')}
                    className="create-first-link-button"
                  >
                    Create Payment Link
                  </button>
                </div>
              ) : (
                paymentLinks.map((link) => (
                  <div key={link.linkId} className="link-card">
                    <div className="link-header">
                      <h3>{formatCurrency(link.amount)}</h3>
                      <span className={`status ${link.status}`}>{link.status.toUpperCase()}</span>
                    </div>
                    
                    <p className="description">{link.description}</p>
                    
                    <div className="link-actions">
                      <div className="link-url-container">
                        <label>Payment Link:</label>
                        <div className="url-input-group">
                          <input 
                            type="text" 
                            value={link.shortUrl} 
                            readOnly 
                            className="link-url"
                          />
                          <button 
                            onClick={() => copyToClipboard(link.shortUrl)}
                            className="copy-button"
                            title="Copy link"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="link-footer">
                      <div className="link-meta">
                        <span>Created: {formatDate(link.createdAt)}</span>
                      </div>
                      
                      <div className="link-controls">
                        <button 
                          onClick={() => window.open(link.shortUrl, '_blank')}
                          className="preview-button"
                        >
                          Preview
                        </button>
                        {link.status === 'created' && (
                          <button 
                            onClick={() => handleCancelLink(link.linkId)}
                            className="cancel-button"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'received-payments' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>Received Payments</h3>
            <button 
              onClick={loadReceivedPayments}
              disabled={loading}
              className="refresh-button"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          
          {loading ? (
            <div className="loading">Loading received payments...</div>
          ) : (
            <div className="payments-section">
              {receivedPayments.length === 0 ? (
                <div className="no-data">
                  <h4>No payments received yet</h4>
                  <p>Once customers start paying through your payment links, they'll appear here.</p>
                </div>
              ) : (
                <>
                  <div className="payments-summary">
                    <div className="summary-card">
                      <h4>Total Received</h4>
                      <div className="summary-amount">
                        {formatCurrency(
                          receivedPayments.reduce((total, payment) => total + Number(payment.amount), 0)
                        )}
                      </div>
                    </div>
                    <div className="summary-card">
                      <h4>Total Transactions</h4>
                      <div className="summary-count">{receivedPayments.length}</div>
                    </div>
                  </div>
                  
                  <div className="payments-table-container">
                    <table className="payments-table">
                      <thead>
                        <tr>
                          <th>Payment Details</th>
                          <th>Customer</th>
                          <th>Amount</th>
                          <th>Product/Service</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receivedPayments.map((payment) => (
                          <tr key={payment.id}>
                            <td>
                              <div className="payment-details">
                                <div className="payment-id">
                                  ID: {payment.razorpayPaymentId?.substring(0, 12)}...
                                </div>
                                <div className="order-id">
                                  Order: {payment.razorpayOrderId?.substring(0, 12)}...
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="customer-info">
                                <div className="customer-name">{payment.customerName}</div>
                                <div className="customer-email">{payment.customerEmail}</div>
                              </div>
                            </td>
                            <td>
                              <div className="amount">{formatCurrency(payment.amount)}</div>
                            </td>
                            <td>
                              <div className="product-name">
                                {payment.productName || 'Payment via link'}
                              </div>
                            </td>
                            <td>
                              <span className={`status ${payment.paymentStatus.toLowerCase()}`}>
                                {payment.paymentStatus}
                              </span>
                            </td>
                            <td>
                              <div className="payment-date">
                                {formatDate(payment.createdAt)}
                              </div>
                            </td>
                            <td>
                              <div className="payment-actions">
                                <button 
                                  onClick={() => copyToClipboard(payment.razorpayPaymentId)}
                                  className="small-button"
                                  title="Copy Payment ID"
                                >
                                  Copy ID
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentCollectionDashboard;